import { webmFixDuration } from 'webm-fix-duration';
import { getSupportedMimeType, BITRATES_BY_QUALITY, AUDIO_BITRATE } from '../utils/browserCompatibility';

export interface StitchingProgress {
  currentScene: number;
  totalScenes: number;
  progress: number;
  status: string;
}

export interface TransitionConfig {
  type: 'none' | 'fade' | 'crossfade';
  duration: number;
}

export class CanvasVideoStitcher {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private onProgress?: (progress: StitchingProgress) => void;
  private recordingStartTime: number = 0;

  constructor(width: number = 1920, height: number = 1080) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx = this.canvas.getContext('2d', { alpha: false })!;
    this.ctx.fillStyle = '#000000';
    this.ctx.fillRect(0, 0, width, height);
  }

  async stitchVideos(
    videoBlobs: Blob[],
    transitions: TransitionConfig[] = [],
    onProgress?: (progress: StitchingProgress) => void
  ): Promise<Blob> {
    console.log('[CanvasVideoStitcher] 🎬 Starting stitch with', videoBlobs.length, 'scenes');
    console.log('[CanvasVideoStitcher] Transitions:', transitions);
    
    this.onProgress = onProgress;
    this.recordedChunks = [];

    const stream = this.canvas.captureStream(30);
    console.log('[CanvasVideoStitcher] Canvas stream created:', {
      active: stream.active,
      tracks: stream.getTracks().length
    });

    // Use optimized codec selection - prioritizes H.264 for hardware encoding
    // @see Base docs/Vimeo Recording Architecture Analysis.md
    const mimeType = getSupportedMimeType();
    const videoBitrate = BITRATES_BY_QUALITY["1080p"]; // Use 1080p bitrate for stitched output
    console.log('[CanvasVideoStitcher] Using codec:', mimeType, 'bitrate:', videoBitrate);

    const recorderOptions: MediaRecorderOptions = {
      videoBitsPerSecond: videoBitrate,
      audioBitsPerSecond: AUDIO_BITRATE,
    };
    
    // Only set mimeType if we have a valid one (guard for unsupported browsers)
    if (mimeType) {
      recorderOptions.mimeType = mimeType;
    }

    this.mediaRecorder = new MediaRecorder(stream, recorderOptions);

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        console.log('[CanvasVideoStitcher] Got data chunk:', event.data.size, 'bytes');
        this.recordedChunks.push(event.data);
      } else {
        console.warn('[CanvasVideoStitcher] Got empty data chunk');
      }
    };

    this.mediaRecorder.onerror = (error) => {
      console.error('[CanvasVideoStitcher] MediaRecorder error:', error);
    };

    const recordingComplete = new Promise<void>((resolve) => {
      this.mediaRecorder!.onstop = () => {
        console.log('[CanvasVideoStitcher] MediaRecorder stopped, total chunks:', this.recordedChunks.length);
        resolve();
      };
    });

    // CRITICAL: Use smaller chunk interval to capture transition frames
    // 100ms ensures we capture smooth transitions (10 chunks per second)
    this.recordingStartTime = Date.now();
    this.mediaRecorder.start(100);
    console.log('[CanvasVideoStitcher] MediaRecorder started with 100ms chunks for smooth transitions');

    for (let i = 0; i < videoBlobs.length; i++) {
      const hasTransition = i < videoBlobs.length - 1 && 
                           transitions[i] && 
                           transitions[i].type !== 'none' && 
                           transitions[i].duration > 0;
      
      console.log(`[CanvasVideoStitcher] Scene ${i + 1}/${videoBlobs.length}`, {
        hasTransition,
        transitionType: hasTransition ? transitions[i].type : 'none',
        transitionDuration: hasTransition ? transitions[i].duration : 0
      });
      
      this.updateProgress(i + 1, videoBlobs.length, `Playing scene ${i + 1}...`);
      
      if (hasTransition) {
        // Play scene MINUS transition duration, then transition to next
        const transitionDuration = transitions[i].duration;
        const transitionType = transitions[i].type as 'fade' | 'crossfade';
        await this.playVideoOnCanvasWithTransition(
          videoBlobs[i], 
          videoBlobs[i + 1], 
          transitionDuration,
          transitionType
        );
        
        // Skip next scene since we already played it during transition
        i++;
        if (i < videoBlobs.length - 1) {
          this.updateProgress(i + 1, videoBlobs.length, `Scene ${i + 1} (via transition)`);
        }
      } else {
        // No transition, play scene fully
        await this.playVideoOnCanvas(videoBlobs[i]);
      }
    }

    console.log('[CanvasVideoStitcher] All scenes played, finalizing MediaRecorder');
    
    // Request final data before stopping to ensure all chunks are flushed
    console.log('[CanvasVideoStitcher] Requesting final data flush...');
    this.mediaRecorder.requestData();
    
    // Wait for the final data event to fire
    await new Promise(resolve => setTimeout(resolve, 200));
    
    console.log('[CanvasVideoStitcher] Stopping MediaRecorder...');
    this.mediaRecorder.stop();
    await recordingComplete;
    
    // Wait for container finalization
    console.log('[CanvasVideoStitcher] Waiting for container finalization...');
    await new Promise(resolve => setTimeout(resolve, 500));
    
    console.log('[CanvasVideoStitcher] Creating final blob with chunks:', this.recordedChunks.length);

    const buggyBlob = new Blob(this.recordedChunks, { type: mimeType });
    console.log('[CanvasVideoStitcher] Buggy blob created (without duration metadata):', {
      size: buggyBlob.size,
      sizeMB: (buggyBlob.size / 1024 / 1024).toFixed(2),
      chunks: this.recordedChunks.length,
      mimeType: mimeType
    });
    
    if (buggyBlob.size === 0) {
      console.error('[CanvasVideoStitcher] ❌ Output video is empty!', {
        totalChunks: this.recordedChunks.length,
        mediaRecorderState: this.mediaRecorder.state
      });
      throw new Error('Stitching failed: Output video is empty. MediaRecorder may not have captured any frames.');
    }

    // FIX WebM duration metadata (critical for QuickTime, Safari, proper seeking)
    const recordingDuration = Date.now() - this.recordingStartTime;
    console.log('[CanvasVideoStitcher] Fixing WebM duration metadata...', {
      durationMs: recordingDuration,
      durationSec: (recordingDuration / 1000).toFixed(2)
    });
    
    const fixedBlob = await webmFixDuration(buggyBlob, recordingDuration, mimeType);
    console.log('[CanvasVideoStitcher] ✓ Final blob created with proper metadata:', {
      size: fixedBlob.size,
      sizeMB: (fixedBlob.size / 1024 / 1024).toFixed(2),
      mimeType: fixedBlob.type
    });

    return fixedBlob;
  }

  private async playVideoOnCanvas(videoBlob: Blob): Promise<void> {
    const video = document.createElement('video');
    video.src = URL.createObjectURL(videoBlob);
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    
    return new Promise<void>((resolve, reject) => {
      let isResolved = false;
      let frameCount = 0;
      
      video.onloadedmetadata = () => {
        console.log('[CanvasVideoStitcher] Video metadata loaded:', {
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight
        });
        
        video.play().catch((err) => {
          console.error('[CanvasVideoStitcher] Play failed:', err);
          reject(err);
        });
      };
      
      video.onerror = (e) => {
        if (!isResolved) {
          isResolved = true;
          console.error('[CanvasVideoStitcher] Video error:', e);
          URL.revokeObjectURL(video.src);
          reject(new Error('Failed to load video'));
        }
      };
      
      const drawFrame = () => {
        if (!video.paused && !video.ended) {
          this.ctx.drawImage(video, 0, 0, this.canvas.width, this.canvas.height);
          frameCount++;
          requestAnimationFrame(drawFrame);
        }
      };
      
      video.onplay = () => {
        console.log('[CanvasVideoStitcher] Video started playing');
        drawFrame();
      };
      
      video.onended = () => {
        if (!isResolved) {
          isResolved = true;
          console.log('[CanvasVideoStitcher] Video playback ended, frames drawn:', frameCount);
          URL.revokeObjectURL(video.src);
          resolve();
        }
      };
      
      setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          const duration = video.duration || 60;
          console.error('[CanvasVideoStitcher] Video playback timeout after', duration + 5, 'seconds');
          URL.revokeObjectURL(video.src);
          reject(new Error(`Video playback timeout (expected: ${duration.toFixed(1)}s)`));
        }
      }, 120000);
    });
  }

  private async playVideoOnCanvasWithTransition(
    currentBlob: Blob,
    nextBlob: Blob,
    transitionDuration: number,
    transitionType: 'fade' | 'crossfade'
  ): Promise<void> {
    console.log(`[CanvasVideoStitcher] 🎨 Starting ${transitionType} transition (${transitionDuration}s)`);
    
    const currentVideo = await this.createVideoElement(currentBlob);
    const nextVideo = await this.createVideoElement(nextBlob);
    
    const currentDuration = currentVideo.duration;
    const nextDuration = nextVideo.duration;
    const transitionStart = Math.max(0, currentDuration - transitionDuration);
    
    console.log('[CanvasVideoStitcher] Transition timing:', {
      currentDuration,
      nextDuration,
      transitionStart,
      transitionDuration
    });
    
    return new Promise((resolve, reject) => {
      let isResolved = false;
      let phase: 'current' | 'transition' | 'next' = 'current';
      let transitionStartTime = 0;
      let frameCount = 0;
      
      const drawFrame = () => {
        if (isResolved) return;
        
        const currentTime = currentVideo.currentTime;
        frameCount++;
        
        if (phase === 'current') {
          this.ctx.drawImage(currentVideo, 0, 0, this.canvas.width, this.canvas.height);
          
          if (currentTime >= transitionStart) {
            console.log('[CanvasVideoStitcher] Entering transition phase at', currentTime.toFixed(2), 's');
            phase = 'transition';
            transitionStartTime = Date.now();
            nextVideo.play().catch(err => {
              console.error('[CanvasVideoStitcher] Next video play failed:', err);
            });
          }
          
          requestAnimationFrame(drawFrame);
        } else if (phase === 'transition') {
          const elapsed = (Date.now() - transitionStartTime) / 1000;
          const progress = Math.min(elapsed / transitionDuration, 1);
          
          if (transitionType === 'fade') {
            if (progress < 0.5) {
              const fadeOut = 1 - (progress * 2);
              this.ctx.fillStyle = '#000000';
              this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
              this.ctx.globalAlpha = fadeOut;
              this.ctx.drawImage(currentVideo, 0, 0, this.canvas.width, this.canvas.height);
              this.ctx.globalAlpha = 1;
            } else {
              const fadeIn = (progress - 0.5) * 2;
              this.ctx.fillStyle = '#000000';
              this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
              this.ctx.globalAlpha = fadeIn;
              this.ctx.drawImage(nextVideo, 0, 0, this.canvas.width, this.canvas.height);
              this.ctx.globalAlpha = 1;
            }
          } else if (transitionType === 'crossfade') {
            this.ctx.globalAlpha = 1 - progress;
            this.ctx.drawImage(currentVideo, 0, 0, this.canvas.width, this.canvas.height);
            this.ctx.globalAlpha = progress;
            this.ctx.drawImage(nextVideo, 0, 0, this.canvas.width, this.canvas.height);
            this.ctx.globalAlpha = 1;
          }
          
          if (progress >= 1) {
            console.log('[CanvasVideoStitcher] Transition complete, entering next phase');
            phase = 'next';
            currentVideo.pause();
          }
          
          requestAnimationFrame(drawFrame);
        } else if (phase === 'next') {
          this.ctx.drawImage(nextVideo, 0, 0, this.canvas.width, this.canvas.height);
          
          if (nextVideo.ended) {
            if (!isResolved) {
              isResolved = true;
              console.log('[CanvasVideoStitcher] ✓ Transition sequence complete, frames drawn:', frameCount);
              URL.revokeObjectURL(currentVideo.src);
              URL.revokeObjectURL(nextVideo.src);
              resolve();
            }
          } else {
            requestAnimationFrame(drawFrame);
          }
        }
      };
      
      currentVideo.onloadedmetadata = () => {
        currentVideo.play().then(() => {
          console.log('[CanvasVideoStitcher] Current video playing');
          drawFrame();
        }).catch(err => {
          console.error('[CanvasVideoStitcher] Current video play failed:', err);
          reject(err);
        });
      };
      
      currentVideo.onerror = (e) => {
        if (!isResolved) {
          isResolved = true;
          console.error('[CanvasVideoStitcher] Current video error:', e);
          reject(new Error('Current video failed'));
        }
      };
      
      nextVideo.onerror = (e) => {
        if (!isResolved) {
          isResolved = true;
          console.error('[CanvasVideoStitcher] Next video error:', e);
          reject(new Error('Next video failed'));
        }
      };
      
      setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          console.error('[CanvasVideoStitcher] Transition timeout');
          reject(new Error('Transition playback timeout'));
        }
      }, 120000);
    });
  }

  private async createVideoElement(blob: Blob): Promise<HTMLVideoElement> {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.src = URL.createObjectURL(blob);
    
    return new Promise((resolve, reject) => {
      video.onloadedmetadata = () => resolve(video);
      video.onerror = () => reject(new Error('Failed to load video'));
      
      setTimeout(() => reject(new Error('Video load timeout')), 10000);
    });
  }

  private updateProgress(current: number, total: number, status: string) {
    if (this.onProgress) {
      this.onProgress({
        currentScene: current,
        totalScenes: total,
        progress: (current / total) * 100,
        status,
      });
    }
  }

  destroy() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.canvas.remove();
  }
}
